/**
 * What the chat does with a message row the duplexer pushed (outside this browser's own SSE turn).
 *
 * - `bubble`: an assistant reply or step, or a USER row this browser did not send — a message the user
 *   typed on their phone (or another tab), a question the meeting room put to the agent, or a "Tell
 *   {agent}" note the server dispatched from a thread or contact composer (`metadata.outreachComposer`).
 * - `status`: a THINKING row. It draws no bubble; it is the live status of a turn this browser is not
 *   streaming — a connect or any other server-started turn — and feeds the thinking line, replacing the
 *   connect placeholder the way it does on iOS and Android.
 * - `ignore`: everything else, including the USER rows this browser sent itself.
 *
 * USER rows from another device used to be ignored along with this browser's own echo, so a photo sent
 * from the phone into a chat open on the desktop never appeared there — only the reply to it did. iOS and
 * Android already draw another device's USER rows live; `sentHere` is what keeps the echo out.
 */
export type LiveChatRowKind = 'bubble' | 'status' | 'ignore';

export function liveChatRowKind(role: string, fromMeetingRoom: boolean, composerNote = false, sentHere = false): LiveChatRowKind {
  if (role === 'ASSISTANT' || role === 'ASSISTANT_STEP' || fromMeetingRoom) return 'bubble';
  if (role === 'USER' && composerNote) return 'bubble';
  if (role === 'USER') return sentHere ? 'ignore' : 'bubble';
  if (role === 'THINKING') return 'status';
  return 'ignore';
}

type SentAttachment = { url?: string };
type SentRow = { id?: string; finalId?: string; role?: string; conversationId?: string; content?: string; attachments?: SentAttachment[] };
type PushedRow = { role?: string; conversationId?: string; content?: string; attachments?: SentAttachment[] };

const attachmentKey = (attachments: SentAttachment[] | undefined) => (attachments ?? []).map((a) => a.url ?? '').sort().join('\n');

/**
 * The id of this browser's own unconfirmed send that a pushed USER row echoes, or undefined.
 *
 * `use-chat` draws every send as an `optimistic-…` row and learns the persisted id only on the post-turn
 * reconcile, so until then the duplexer twin is matched by what the row carries: the same conversation, the
 * same text and the same uploaded files (a caption-less photo has no text, so the files are what tell it from
 * one the phone sent). The caller CONSUMES the match by stamping the row's `finalId`, so one local send
 * swallows exactly one echo and an identical message from another device after it is still drawn. A row that
 * already knows its server id is left to the caller's id check.
 */
export function ownUnconfirmedSendId(rows: SentRow[], entity: PushedRow): string | undefined {
  if (entity.role !== 'USER') return undefined;
  const text = entity.content ?? '';
  const files = attachmentKey(entity.attachments);
  return rows.find(
    (m) =>
      m.role === 'USER' &&
      !m.finalId &&
      !!m.id?.startsWith('optimistic-') &&
      // A first send draws its row before the conversation exists, so an empty id is this thread's.
      (!m.conversationId || !entity.conversationId || m.conversationId === entity.conversationId) &&
      (m.content ?? '') === text &&
      attachmentKey(m.attachments) === files,
  )?.id;
}
